import React, { useState } from "react";

import { mdiEye, mdiOpenInNew } from "@mdi/js";
import Icon from "@mdi/react";
import classnames from "classnames";
import { useTranslation } from "react-i18next";

import useOutsideClick from "../../hooks/useOutsideClick";
import Tooltip from "../tooltip/Tooltip";

import type { JSX } from "react";

export const BUTTON_TYPES = {
  BUTTON: "button",
  SUBMIT: "submit",
  RESET: "reset",
} as const;

type ButtonTypeKeys = keyof typeof BUTTON_TYPES;

type ButtonTypes = (typeof BUTTON_TYPES)[ButtonTypeKeys];

export const BUTTON_VARIANTS = {
  DELETE: "delete",
  PRIMARY: "primary",
  SECONDARY: "secondary",
  GHOST: "ghost",
} as const;

type ButtonVariantKeys = keyof typeof BUTTON_VARIANTS;

export type ButtonVariants = (typeof BUTTON_VARIANTS)[ButtonVariantKeys];

export const BUTTON_SIZES = {
  XS: "xs",
  SM: "sm",
  MD: "md",
  LG: "lg",
} as const;

type ButtonSizeKeys = keyof typeof BUTTON_SIZES;

type ButtonSizes = (typeof BUTTON_SIZES)[ButtonSizeKeys];

interface BaseProps {
  type: ButtonTypes;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  isDisabled?: boolean;
  variant?: ButtonVariants;
  size?: ButtonSizes;
  isFullWidth?: boolean;
  isFullHeight?: boolean;
  "data-tooltip-id"?: string;
  "data-tooltip-content"?: string;
  "data-tooltip-hidden"?: boolean;
  form?: string;
  "data-testid"?: string;
  withGlow?: boolean;
}

interface ButtonWithChildren extends BaseProps {
  children: React.ReactNode;
  dropdownItemList?: {
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
    className?: string;
    isDisabled?: boolean;
  }[];

  externalUrl?: never;
}

interface ButtonWithExternalUrl extends BaseProps {
  externalUrl: string;
  children?: never;
  dropdownItemList?: never;
}

type ButtonProps = ButtonWithChildren | ButtonWithExternalUrl;

export default function Button({
  children,
  type = BUTTON_TYPES.BUTTON,
  onClick,
  className,
  isDisabled,
  variant,
  isFullWidth,
  isFullHeight,
  size = BUTTON_SIZES.LG,
  form,
  externalUrl,
  "data-tooltip-id": dataTooltipId,
  "data-tooltip-content": dataTooltipContent,
  "data-tooltip-hidden": dataTooltipHidden,
  dropdownItemList,
  "data-testid": dataTestId,
  withGlow,
}: ButtonProps): JSX.Element {
  const [showDropdown, setShowDropdown] = useState(false);
  const buttonContainerRef = useOutsideClick(() => {
    if (showDropdown) {
      setShowDropdown(false);
    }
  });

  const { t } = useTranslation("editor-utils");

  const btnClassNames = classnames(
    "flex items-center justify-center gap-[0.46875rem] rounded-lg transition-all disabled:cursor-not-allowed disabled:opacity-50 disabled:grayscale-[50%] cursor-pointer",
    (!variant || variant === BUTTON_VARIANTS.PRIMARY) &&
      "hover:enabled:bg-primary/90 bg-primary active:bg-primary/80 text-primary-foreground border border-transparent",

    variant === BUTTON_VARIANTS.DELETE &&
      "hover:enabled:bg-destructive/90 bg-destructive active:bg-destructive/80 text-primary-foreground",
    variant === BUTTON_VARIANTS.SECONDARY &&
      "border border-border bg-card text-foreground hover:border-input hover:bg-muted active:bg-surface-muted disabled:bg-muted disabled:text-muted-foreground",
    variant === BUTTON_VARIANTS.GHOST &&
      "bg-transparent hover:bg-muted active:bg-surface-muted border-0",

    size === BUTTON_SIZES.LG && "p-[0.78125rem] text-lg font-semibold",
    size === BUTTON_SIZES.MD &&
      "px-[0.78125rem] py-[0.390625rem] text-base font-medium",
    size === BUTTON_SIZES.SM && "p-[0.46875rem] text-sm",
    size === BUTTON_SIZES.XS && "p-1.5 text-xs",

    isFullWidth && "w-full",
    isFullHeight && "h-full",
    withGlow && "shadow-glow",
    className,
  );

  const buttonContainerClassNames = classnames(
    "relative",
    isFullWidth && "w-full",
    isFullHeight && "h-full",
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    if (externalUrl) return;

    if (dropdownItemList) {
      setShowDropdown(!showDropdown);
    } else {
      onClick?.(event);
    }
  };

  const hasTooltip = dataTooltipId && dataTooltipContent;

  const renderExternalUrl = (): React.ReactNode => (
    <React.Fragment>
      <Icon path={mdiEye} size={0.78125} />
      <a
        className="hover:text-foreground"
        href={externalUrl}
        rel="noreferrer"
        target="_blank"
      >
        {t("viewLink")}
      </a>
      <Icon className="self-start" path={mdiOpenInNew} size={0.4375} />
    </React.Fragment>
  );

  return (
    <div ref={buttonContainerRef} className={buttonContainerClassNames}>
      <button
        className={btnClassNames}
        data-testid={dataTestId}
        data-tooltip-id={dataTooltipId}
        disabled={isDisabled}
        form={form}
        onClick={handleClick}
        type={type} // eslint-disable-line react/button-has-type
      >
        {externalUrl ? renderExternalUrl() : children}
      </button>
      {showDropdown ? (
        <div className="absolute top-full right-0 z-20 min-w-full">
          <ul className="border-border bg-card rounded-[0.3125rem] border whitespace-nowrap drop-shadow-lg">
            {dropdownItemList?.map((item) => {
              const itemClassNames = classnames(
                "flex w-full cursor-pointer items-center gap-2.5 p-2.5 text-left hover:bg-muted",
                item.className,
              );
              return (
                <li key={item.label}>
                  <button
                    className={itemClassNames}
                    disabled={item.isDisabled}
                    type="button"
                    onClick={(): void => {
                      item.onClick();
                      setShowDropdown(false);
                    }}
                  >
                    {item.icon}
                    {item.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
      {hasTooltip ? (
        <Tooltip
          content={dataTooltipContent}
          hidden={dataTooltipHidden}
          id={dataTooltipId}
        />
      ) : null}
    </div>
  );
}
